import { isEmpty } from 'lodash';

import type { RequestError } from '@swf/core/api/request';
import type { EffectParams } from '@swf/core/store';
import type { Account, AccountLocales, OddsFormat, Response } from '@swf/core/types/Account';
import type { TournamentHistoryRequestType } from '@swf/core/types/History';
import type { ErrorCollection } from '@swf/core/types/Response';
import type { PlayMoneyRefillResult } from '@swf/generated/types/api/v0/RAM2';

export interface HandHistoryPayload {
  requestType: number;
  param?: number;
  handId?: number;
  numRequested?: number;
  locale: number;
}

export interface CasinoGameHistoryPayload {
  requestType: string;
  numOfHands?: number;
  numOfSeconds?: number;
  handId?: number;
  locale: number;
  sendByEmail?: boolean;
}

const safeRequest = async (
  { service, payload }: { service: string; payload: object },
  { request }: EffectParams
): Promise<Response> => {
  try {
    await request({ service, body: payload });
    return { success: true };
  } catch (e) {
    const error = e as RequestError<ErrorCollection>;
    return {
      statusCode: error?.headers?.statusCode,
      success: false,
      message: isEmpty(error.body) ? error.message : error.body.message,
      errors: error.body
    };
  }
};

const effects = {
  async fetch(payload: void, { dispatch, request }: EffectParams) {
    const { body } = await request({ service: 'account.info' });
    dispatch.account.update(body);
  },
  requestHandHistory(payload: HandHistoryPayload, params: EffectParams) {
    return safeRequest({ service: 'history.clientHistory', payload }, params);
  },
  requestCasinoHandHistory(payload: CasinoGameHistoryPayload, params: EffectParams) {
    return safeRequest(
      { service: 'history.casinoHandHistory', payload: { ...payload, sendByEmail: true } },
      params
    );
  },
  requestPlayingHistory(
    payload: {
      from: string;
      to: string;
      zipPassword: string;
      includeFpp: boolean;
      reportFormat: string;
      password: string;
    },
    params: EffectParams
  ) {
    return safeRequest({ service: 'history.audit', payload }, params);
  },

  requestTournamentHistory(
    { type, tournamentId: tournId }: { type: TournamentHistoryRequestType; tournamentId: string },
    params: EffectParams
  ) {
    return safeRequest({ service: 'history.tournHistory', payload: { type, tournId } }, params);
  },
  async updateAddress(
    address: {
      streetAddress?: string;
      city: string;
      state: string;
      zip: string;
      country: string;
      buildingNumber?: string;
      streetName?: string;
    },
    params: EffectParams
  ) {
    const response = await safeRequest(
      { service: 'account.info.updateAddress', payload: address },
      params
    );
    await params.dispatch.account.fetch();
    return response;
  },
  async updateCommunicationPreferences(
    preferences: {
      mailListPoker: boolean;
      mailListCasino: boolean;
      mailListSports: boolean;
      liveAlertSport: boolean;
    },
    params: EffectParams
  ) {
    const response = await safeRequest(
      { service: 'account.info.updateCommPreferences', payload: preferences },
      params
    );
    await params.dispatch.account.fetch();
    return response;
  },
  async updateEmail(settings: { email: Account['email'] }, params: EffectParams) {
    const response = await safeRequest(
      { service: 'account.info.updateEmail', payload: settings },
      params
    );
    await params.dispatch.account.fetch();
    return response;
  },
  async validateEmailCode(payload: { code: number }, params: EffectParams) {
    const response = await safeRequest({ service: 'account.validateEmailCode', payload }, params);
    await params.dispatch.account.fetch();
    return response;
  },
  sendValidationEmail(payload: void, { request }: EffectParams) {
    return request({ service: 'account.sendValidationEmail' });
  },
  updateEmailLocaleSettings(settings: AccountLocales, params: EffectParams) {
    return safeRequest({ service: 'account.info.emlocales.update', payload: settings }, params);
  },
  async updateTimezone(
    settings: { timezone?: string; timezoneName?: string },
    params: EffectParams
  ) {
    const response = await safeRequest(
      { service: 'account.info.timezone.update', payload: settings },
      params
    );
    await params.dispatch.account.fetch();
    return response;
  },
  async updateMultiCurrencyPreferences(
    preferences: { convertBuyIns: boolean; convertWinnings: boolean },
    params: EffectParams
  ) {
    const response = await safeRequest(
      { service: 'account.info.updateCurrencyConversionSettings', payload: preferences },
      params
    );
    await params.dispatch.account.fetch();
    return response;
  },
  updatePassword(payload: { password: string; oldPassword: string }, params: EffectParams) {
    return safeRequest({ service: 'auth.pwd.change', payload }, params);
  },
  async updatePhones(
    payload: { mobilePhone?: Account['mobilePhone']; phone?: Account['phone'] },
    params: EffectParams
  ) {
    const response = await safeRequest({ service: 'account.info.updatePhones', payload }, params);
    await params.dispatch.account.fetch();
    return response;
  },
  updatePlayChips(payload: void, { request }: EffectParams) {
    return request<PlayMoneyRefillResult>({ service: 'account.playMoneyRefillInfo' });
  },
  updateDob(payload: { dob: string }, params: EffectParams) {
    return safeRequest({ service: 'account.info.updateDob', payload }, params);
  },
  async updateOddsFormat(payload: { oddsFormat: OddsFormat }, params: EffectParams) {
    await safeRequest({ service: 'account.info.updateOddsFormat', payload }, params);
    await params.dispatch.account.fetch();
  },
  async updateAutoAcceptPrice(payload: { autoAcceptPriceChanges: boolean }, params: EffectParams) {
    await safeRequest({ service: 'account.info.updateAutoAcceptPriceChanges', payload }, params);
    await params.dispatch.account.fetch();
  },
  async updateFirstNameLastName(
    payload: {
      firstName: string;
      lastName: string;
    },
    params: EffectParams
  ) {
    const response = await safeRequest(
      { service: 'account.info.updateFirstNameLastName', payload },
      params
    );
    await params.dispatch.account.fetch();
    return response;
  },
  updateRealMoneyFlag(payload: void, { request }: EffectParams) {
    return request({ service: 'account.info.enableRealMoneyFlag' });
  }
};

export default effects;
